import React, { useState } from 'react';
import type { QuoteFormData } from '../../types/quote';
import {
  FiCheckCircle,
  FiCopy,
  FiCheck,
  FiPrinter,
  FiDownload,
  FiRotateCcw,
  FiArrowRight,
  FiPhone,
  FiMail,
  FiMapPin,
  FiCalendar,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { downloadOrderSummaryPdf } from '../../utils/pdfGenerator';

interface QuoteSuccessProps {
  data: QuoteFormData;
  onReset: () => void;
}

export const QuoteSuccess: React.FC<QuoteSuccessProps> = ({ data, onReset }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyReference = () => {
    if (data.referenceNumber) {
      navigator.clipboard.writeText(data.referenceNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    downloadOrderSummaryPdf(data);
  };

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 sm:p-10 md:p-12 transition-all">
      {/* Success Badge & Header */}
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 ring-8 ring-emerald-50 shadow-inner">
          <FiCheckCircle className="w-10 h-10 stroke-[2.5]" />
        </div>

        <span className="text-xs uppercase tracking-widest text-[#F55109] font-bold bg-[#F55109]/10 px-3.5 py-1.5 rounded-full inline-block mb-3">
          Request Successfully Submitted
        </span>

        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#2C2C2C] special-header mb-4">
          TRANSPORTATION REQUEST RECEIVED
        </h2>

        {/* The Exact Confirmation message from requirements */}
        <div className="p-5 sm:p-6 bg-orange-50/50 rounded-2xl border border-orange-100 text-gray-800 text-base sm:text-lg leading-relaxed shadow-xs">
          Thank you for submitting your transportation request to Speedyssey. Your request has been
          received. Our team will review the details and contact you regarding availability,
          pricing, and next steps.
        </div>
      </div>

      {/* Reference number banner */}
      <div className="max-w-xl mx-auto bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div>
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">
            Request Reference ID
          </span>
          <span className="text-xl sm:text-2xl font-mono font-bold text-gray-900 tracking-wider">
            {data.referenceNumber}
          </span>
          <span className="text-xs text-gray-500 block mt-0.5">
            Submitted on {data.submittedAt || new Date().toLocaleString()}
          </span>
        </div>

        <button
          type="button"
          onClick={handleCopyReference}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-gray-100 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 shadow-2xs transition-colors cursor-pointer"
        >
          {copied ? (
            <>
              <FiCheck className="w-4 h-4 text-emerald-600" />
              <span className="text-emerald-600">Copied!</span>
            </>
          ) : (
            <>
              <FiCopy className="w-4 h-4" />
              <span>Copy ID</span>
            </>
          )}
        </button>
      </div>

      {/* Email Notification Notice */}
      <div className="max-w-3xl mx-auto bg-gradient-to-r from-blue-50/90 to-indigo-50/90 border border-blue-200/80 rounded-2xl p-4 sm:p-5 mb-8 flex items-start gap-3.5 shadow-xs">
        <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-sm">
          <FiMail className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-bold text-blue-950">
              Summary PDF Dispatched to Your Email
            </h4>
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
              Email &amp; PDF Sent
            </span>
          </div>
          <p className="text-xs sm:text-sm text-blue-900 mt-1 leading-relaxed">
            An official PDF copy of this transportation summary has been emailed to <strong className="font-semibold text-blue-950">{data.email}</strong>. Our logistics operations team and admins have also received an immediate dispatch alert.
          </p>
        </div>
      </div>

      {/* Key Request Summary */}
      <div className="max-w-3xl mx-auto bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 mb-10 shadow-xs">
        <h4 className="text-lg font-bold text-gray-900 special-header pb-4 border-b border-gray-100 mb-6">
          REQUEST SUMMARY
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          {/* Customer */}
          <div>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
              Customer Contact
            </span>
            <p className="font-bold text-gray-900">{data.fullName}</p>
            {data.companyName && <p className="text-gray-600">{data.companyName}</p>}
            <p className="text-gray-600 flex items-center gap-1.5 mt-1">
              <FiPhone className="w-3.5 h-3.5 text-gray-400" /> {data.phoneNumber}
            </p>
            <p className="text-gray-600 flex items-center gap-1.5 mt-0.5">
              <FiMail className="w-3.5 h-3.5 text-gray-400" /> {data.email}
            </p>
          </div>

          {/* Schedule */}
          <div>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
              Pickup Schedule
            </span>
            <p className="font-bold text-gray-900 flex items-center gap-1.5">
              <FiCalendar className="w-4 h-4 text-[#F55109]" /> {data.preferredPickupDate}
            </p>
            <p className="text-gray-600 mt-1">
              Time: {data.preferredPickupTime || 'Flexible / Any time'}
            </p>
            <p className="text-gray-600 mt-0.5">
              Handling:{' '}
              {data.specialHandling && data.specialHandling.length > 0
                ? data.specialHandling.join(', ')
                : 'Standard'}
            </p>
          </div>

          {/* Pickup Address */}
          <div className="pt-4 border-t border-gray-100">
            <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wider flex items-center gap-1 mb-1">
              <FiMapPin className="w-3.5 h-3.5" /> Pickup From
            </span>
            <p className="font-semibold text-gray-800 whitespace-pre-line">{data.pickupAddress}</p>
            <p className="text-xs text-gray-500 mt-1">
              Contact: {data.pickupContactName} ({data.pickupContactPhone})
            </p>
          </div>

          {/* Delivery Address */}
          <div className="pt-4 border-t border-gray-100">
            <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider flex items-center gap-1 mb-1">
              <FiMapPin className="w-3.5 h-3.5" /> Deliver To
            </span>
            <p className="font-semibold text-gray-800 whitespace-pre-line">{data.deliveryAddress}</p>
            <p className="text-xs text-gray-500 mt-1">
              Receiver: {data.receiverName} ({data.receiverPhoneNumber})
            </p>
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3.5 max-w-2xl mx-auto pt-2">
        <button
          type="button"
          onClick={handleDownloadPdf}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#F55109] hover:bg-[#d44304] text-white font-semibold rounded-xl shadow-md transition-all cursor-pointer hover:shadow-lg"
        >
          <FiDownload className="w-4 h-4" />
          <span>Download Summary (PDF)</span>
        </button>

        <button
          type="button"
          onClick={handlePrint}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold rounded-xl transition-colors cursor-pointer"
        >
          <FiPrinter className="w-4 h-4" />
          <span>Print / Save</span>
        </button>

        <button
          type="button"
          onClick={onReset}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 border-2 border-gray-200 hover:border-gray-300 text-gray-700 font-semibold rounded-xl transition-colors cursor-pointer"
        >
          <FiRotateCcw className="w-4 h-4" />
          <span>Submit Another</span>
        </button>

        <Link
          to="/"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#2C2C2C] hover:bg-black text-white font-semibold rounded-xl shadow-md transition-colors cursor-pointer"
        >
          <span>Return Home</span>
          <FiArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};
